"use client"

import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Mobile, PC, Tablet, Notebook } from "../../../views/ReactiveMode"
import PageHeader from "../../../components/common/PageHeader"
import SearchBox from "../../../components/common/SearchBox"
import EmptyState from "../../../components/common/EmptyState"
import { FaUsers, FaPlus, FaEdit, FaEllipsisV, FaTrash } from "react-icons/fa"
import actionTypes from "../../../constants/actionTypes"

// 동아리 전용 메뉴 ID
const CLUBS_MENU_ID = 21


// ID 유효성 검사 함수
const isValidId = (id) => {
    if (!id) return false
    const idStr = String(id)
    return (
        !idStr.startsWith("temp_") && !idStr.startsWith("error_") && idStr.trim() !== "" && id !== null && id !== undefined
    )
}

// HTML 문자열에서 메타데이터 추출 함수
const extractMetadataFromHTML = (htmlString, title = "") => {
    try {

        if (!htmlString || typeof htmlString !== "string") {
            return {
                category: "기타",
                description: "",
                members: 0,
            }
        }

        const parser = new DOMParser()
        const doc = parser.parseFromString(htmlString, "text/html")

        // club-description 추출
        const descElem = doc.querySelector(".club-description")
        const description = descElem ? descElem.textContent.trim() : ""

        // club-category 추출
        let category = "기타"
        const categoryElem = doc.querySelector(".club-category")
        if (categoryElem) {
            category = categoryElem.textContent.trim()
        }

        // 회원 수 추출
        let members = 0
        const detailItems = doc.querySelectorAll(".detail-item")

        for (const item of detailItems) {
            const label = item.querySelector(".detail-label")
            const value = item.querySelector(".detail-value")


            if (label?.textContent.includes("회원 수") && value) {
                const match = value.textContent.match(/\d+/)
                if (match) {
                    members = parseInt(match[0], 10)
                    break
                }
            }
        }

        return {
            category,
            description,
            members,
        }
    } catch (err) {
        return {
            category: "기타",
            description: title || "",
            members: 0,
        }
    }
}

function Clubs() {
    const [searchTerm, setSearchTerm] = useState("")
    const [showDropdown, setShowDropdown] = useState({})

    // 데이터 로드 상태 관리
    const hasLoadedRef = useRef(false)
    const mountedRef = useRef(false)

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    // Redux 상태 - 메뉴 ID별로 분리된 상태 선택
    const boardState = useSelector((state) => state.board)
    const loginData = useSelector((state) => state.login)
    const clubsBoardData = boardState.boards[CLUBS_MENU_ID] || {
        loading: false,
        error: null,
        data: { content: [], totalPages: 0, totalElements: 0, number: 0, size: 10 },
    }

    const { loading, data: boardData, error } = clubsBoardData

    // 관리자 권한 체크 함수
    const hasAdminPermission = useCallback(() => {
        if (!loginData) {
            return false
        }

        const { isLogin, role } = loginData


        if (!isLogin || !role) {
            return false
        }

        const adminRoles = ["ROLE_WEB_ADMIN", "ROLE_ADMIN"]
        const hasPermission = adminRoles.includes(role)

        return hasPermission
    }, [loginData])

    // 데이터 로드 함수 (메모이제이션으로 참조 안정성 보장)
    const loadClubsData = useCallback(() => {
        // actionTypes가 제대로 import되었는지 확인
        if (!actionTypes.BOARD_FETCH) {
            console.error("actionTypes.BOARD_FETCH가 정의되지 않았습니다:", actionTypes)
            return
        }

        // 이미 로딩 중이면 중복 호출 방지
        if (loading) {
            return
        }


        dispatch({
            type: actionTypes.BOARD_FETCH,
            payload: {
                menuId: CLUBS_MENU_ID,
                page: 0,
                size: 100,
            },
        })

        hasLoadedRef.current = true
    }, [dispatch, loading])

    // 컴포넌트 마운트 시 한 번만 데이터 로드
    useEffect(() => {
        mountedRef.current = true

        // /clubs 경로가 아니면 실행하지 않음
        if (location.pathname !== "/clubs") {
            return
        }

        // 이미 로드했거나 로딩 중이면 스킵
        if (hasLoadedRef.current || loading) {
            return
        }

        // 데이터가 이미 있으면 스킵 (새로고침이 아닌 경우)
        if (boardData?.content && boardData.content.length > 0) {
            hasLoadedRef.current = true
            return
        }

        loadClubsData()

        return () => {
            mountedRef.current = false
        }
    }, []) // 빈 의존성 배열로 마운트 시에만 실행

    // 경로 변경 시 처리 (별도 useEffect)
    useEffect(() => {
        if (location.pathname === "/clubs" && mountedRef.current) {
            // 글 작성 후 돌아온 경우에만 새로 로드
            if (location.state?.fromWrite) {
                hasLoadedRef.current = false
                loadClubsData()
            }
        }
    }, [location.pathname, location.state, loadClubsData])

    // board 데이터를 동아리 형태로 변환 (메모이제이션)
    const clubs = useMemo(() => {
        if (!boardData?.content || !Array.isArray(boardData.content)) {
            return []
        }

        return boardData.content.map((item, index) => {
            try {
                let clubId = null
                const idFields = ["id", "contentId", "boardContentId", "boardId", "no", "seq", "idx"]

                for (const field of idFields) {
                    if (item[field] && item[field] !== null && item[field] !== undefined) {
                        clubId = item[field]
                        break
                    }
                }

                if (!clubId) {
                    clubId = `temp_${Date.now()}_${index}`
                }

                const metadata = extractMetadataFromHTML(item.contents, item.title)
                const isHTMLContent = item.contents && typeof item.contents === "string" && item.contents.trim().startsWith("<")

                return {
                    id: clubId,
                    name: item.title || `동아리 ${index + 1}`,
                    category: metadata.category || "기타",
                    description: metadata.description || "설명이 없습니다.",
                    members: metadata.members || 0,
                    createdDate: item.createdDate,
                    modifiedDate: item.modifiedDate,
                    isHTML: isHTMLContent,
                }
            } catch (error) {
                console.error(`동아리 ${index} 데이터 파싱 오류:`, error, item)
                return {
                    id: `error_${Date.now()}_${index}`,
                    name: item.title || `동아리 ${index + 1}`,
                    category: "기타",
                    description: "동아리 정보를 불러오는 중 오류가 발생했습니다.",
                    members: 0,
                    isHTML: false,
                }
            }
        })
    }, [boardData?.content])

    // 검색어에 따라 동아리 필터링 (메모이제이션)
    const filteredClubs = useMemo(() => {
        if (!searchTerm.trim()) {
            return clubs
        }

        const searchLower = searchTerm.toLowerCase()
        return clubs.filter(
            (club) =>
                club.name.toLowerCase().includes(searchLower) ||
                club.description.toLowerCase().includes(searchLower) ||
                club.category.toLowerCase().includes(searchLower),
        )
    }, [clubs, searchTerm])

    // 검색 핸들러 (메모이제이션)
    const handleSearch = useCallback((term) => {
        setSearchTerm(term)
    }, [])

    // 동아리 클릭 핸들러 (메모이제이션)
    const handleClubClick = useCallback(
        (club) => {
            if (!isValidId(club.id)) {
                alert(`동아리 정보에 오류가 있습니다.
관리자에게 문의해주세요.`)
                return
            }
            navigate(`/clubs/detail/${club.id}`)
        },
        [navigate],
    )

    // 글쓰기 버튼 핸들러
    const handleWriteClick = useCallback(() => {
        if (!hasAdminPermission()) {
            alert("동아리 등록 권한이 없습니다.")
            return
        }
        navigate("/clubs/write")
    }, [navigate, hasAdminPermission])

    // 드롭다운 토글
    const toggleDropdown = useCallback((clubId) => {
        setShowDropdown((prev) => ({
            ...prev,
            [clubId]: !prev[clubId],
        }))
    }, [])

    // 수정 버튼 핸들러
    const handleDropdownEdit = useCallback(
        (clubId, e) => {
            e.stopPropagation()

            if (!isValidId(clubId)) {
                alert("동아리 정보에 오류가 있습니다.")
                return
            }

            navigate(`/clubs/edit/${clubId}`)
            setShowDropdown({})
        },
        [navigate],
    )

    // 삭제 버튼 핸들러
    const handleDropdownDelete = useCallback(
        (club, e) => {
            e.stopPropagation()

            if (!isValidId(club.id)) {
                alert("동아리 정보에 오류가 있습니다.")
                return
            }

            if (window.confirm(`"${club.name}" 동아리를 삭제하시겠습니까?`)) {
                dispatch({
                    type: actionTypes.BOARD_DELETE,
                    payload: {
                        id: club.id,
                        menuId: CLUBS_MENU_ID,
                    },
                })
                setShowDropdown({})
                // 삭제 후 목록 새로고침
                setTimeout(() => {
                    loadClubsData()
                }, 500)
            }
        },
        [dispatch, loadClubsData],
    )

    // 동아리 카드 컴포넌트
    const ClubCard = useCallback(
        ({ club }) => {
            return (
                <div className="club-card no-image" onClick={() => handleClubClick(club)}>
                    <div className="club-content">
                        <div className="club-header">
                            <h3 className="club-name">{club.name}</h3>
                            {hasAdminPermission() && (
                                <div className="club-actions">
                                    <button
                                        className="club-action-btn"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            toggleDropdown(club.id)
                                        }}
                                    >
                                        <FaEllipsisV />
                                    </button>
                                    {showDropdown[club.id] && (
                                        <div className="club-dropdown">
                                            <button onClick={(e) => handleDropdownEdit(club.id, e)} className="dropdown-item edit">
                                                <FaEdit /> 수정
                                            </button>
                                            <button onClick={(e) => handleDropdownDelete(club, e)} className="dropdown-item delete">
                                                <FaTrash /> 삭제
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <p className="club-description">{club.description}</p>

                        <div className="club-info">
                            <div className="club-info-item">
                                <FaUsers className="info-icon" />
                                <span>{club.members}명</span>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        [handleClubClick, toggleDropdown, handleDropdownEdit, handleDropdownDelete, showDropdown, hasAdminPermission],
    )

    // 빈 상태 컴포넌트
    const EmptyStateComponent = useCallback(
        () => (
            <div className="empty-state">
                <div className="empty-state-icon">
                    <FaUsers size={48} color="#ccc" />
                </div>
                <h3 className="empty-state-title">아직 등록된 동아리가 없습니다</h3>
                <p className="empty-state-description">
                    교회의 다양한 동아리 활동을
                    <br />
                    소개하고 함께 참여해보세요.
                    <br />첫 번째 동아리를 등록해보세요!
                </p>
                {hasAdminPermission() && (
                    <button className="empty-state-button" onClick={handleWriteClick}>
                        <FaPlus /> 첫 동아리 등록하기
                    </button>
                )}
            </div>
        ),
        [handleWriteClick, hasAdminPermission],
    )

    // 로딩 컴포넌트
    const LoadingComponent = () => (
        <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>동아리 정보를 불러오는 중...</p>
        </div>
    )

    // 메인 컨텐츠 렌더링
    const renderContent = () => {
        if (loading) {
            return <LoadingComponent />
        }

        if (error) {
            return <EmptyState message="오류가 발생했습니다" description={error} />
        }

        if (filteredClubs.length === 0) {
            return searchTerm ? (
                <EmptyState
                    message="검색 결과가 없습니다"
                    description={`"${searchTerm}"에 대한 검색 결과를 찾을 수 없습니다.`}
                />
            ) : (
                <EmptyStateComponent />
            )
        }

        return (
            <div className="clubs-grid">
                {filteredClubs.map((club) => (
                    <ClubCard key={club.id} club={club} />
                ))}
            </div>
        )
    }

    return (
        <>
            <PC>
                <section className="pf-page">
                    <div className="clubs-container">
                        <div className="clubs-header">
                            {hasAdminPermission() && (
                                <button className="write-button" onClick={handleWriteClick}>
                                    <FaPlus /> 동아리 등록
                                </button>
                            )}
                        </div>
                        {renderContent()}
                    </div>
                </section>
            </PC>

            <Notebook>
                <section className="pf-page">
                    <div className="clubs-container">
                        <div className="clubs-header">

                            {hasAdminPermission() && (
                                <button className="write-button" onClick={handleWriteClick}>
                                    <FaPlus /> 동아리 등록
                                </button>
                            )}
                        </div>
                        {renderContent()}
                    </div>
                </section>
            </Notebook>

            <Tablet>
                <section className="pf-page">
                    <div className="clubs-container">
                        <div className="clubs-header">

                            {hasAdminPermission() && (
                                <button className="write-button" onClick={handleWriteClick}>
                                    <FaPlus /> 동아리 등록
                                </button>
                            )}
                        </div>
                        {renderContent()}
                    </div>
                </section>
            </Tablet>

            <Mobile>
                <section className="mf-page">
                    <div className="clubs-container">
                        <div className="clubs-header">
                            {hasAdminPermission() && (
                                <button className="write-button mobile" onClick={handleWriteClick}>
                                    <FaPlus /> 등록
                                </button>
                            )}
                        </div>
                        {renderContent()}
                    </div>
                </section>
            </Mobile>

            <style>{`
        .club-dropdown .dropdown-item.delete {
          color: #dc3545;
        }

        .club-dropdown .dropdown-item.delete:hover {
          background-color: #fff5f5;
        }
      `}</style>
        </>
    )
}

export default Clubs
